import React, { useMemo, useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import ArticleIcon from '@mui/icons-material/Article';
import CompressIcon from '@mui/icons-material/Compress';
import BallotIcon from '@mui/icons-material/Ballot';
import FireExtinguisherIcon from '@mui/icons-material/FireExtinguisher';
import {
  Button,
  FileUpload,
  Form,
  FormItem,
  Input,
  RadioGroup,
  SubmitButton,
  Loading,
} from '../../../../../components';
import { SearchIcon } from '../../../../../components/SearchBar/SearchIcon';
import { useFetch } from '../../../../../hooks';
import { getScrapingList, getTasksList } from '../../../../../api';

type SourceType =
  | 'pdf'
  | 'zip'
  | 'text'
  | 'scraped'
  | 'compressed'
  | 'firecrawl';

const sourceTypes: {
  label: string;
  value: SourceType;
  icon: React.ReactNode;
}[] = [
  {
    label: 'PDF',
    value: 'pdf',
    icon: <PictureAsPdfIcon fontSize="large" className="text-inherit" />,
  },
  {
    label: 'Zip',
    value: 'zip',
    icon: <FolderZipIcon fontSize="large" className="text-inherit" />,
  },
  {
    label: 'Free text',
    value: 'text',
    icon: <ArticleIcon fontSize="large" className="text-inherit" />,
  },
  {
    label: 'Scraping',
    value: 'scraped',
    icon: <BallotIcon fontSize="large" className="text-inherit" />,
  },
  {
    label: 'Compression',
    value: 'compressed',
    icon: <CompressIcon fontSize="large" className="text-inherit" />,
  },
  {
    label: 'Firecrawl JSON',
    value: 'firecrawl',
    icon: <FireExtinguisherIcon fontSize="large" className="text-inherit" />,
  },
];

type Props = {
  isSourceCreating: boolean;
  onCancel: () => void;
  handleUploadSourceFile: (value: any) => void;
  handleUploadSourceText: (value: any) => void;
  handleUploadSourceFirecrawl: (value: any) => void;
};

type FormProps = {
  file: File | null;
  text: string | null;
  task: string | null;
  firecrawl: string | null;
};

const defaultFormValues: FormProps = {
  file: null,
  text: null,
  task: null,
  firecrawl: null,
};

export const KnowledgeSourceForm: React.FC<Props> = ({
  isSourceCreating,
  handleUploadSourceFile,
  handleUploadSourceText,
  handleUploadSourceFirecrawl,
  onCancel,
}) => {
  const [selectedType, setSelectedType] = useState<SourceType>('pdf');
  const [formValues, setFormValues] = useState<FormProps>(defaultFormValues);
  const [searchTaskValue, setSearchTask] = useState('');

  const { loading: isLoadingTasks, data } = useFetch(async () => {
    let data = null;
    switch (selectedType) {
      case 'scraped':
        return (data = await getScrapingList(1, 100)).content?.[0].tasks;
      case 'compressed':
        return (data = await getTasksList(1, 100, 'compress')).content;
      default:
        break;
    }
    return data;
  }, [selectedType]);

  const filteredData = useMemo(
    () =>
      data &&
      data?.filter((item: any) => {
        if (searchTaskValue) {
          return item.name
            .toLowerCase()
            .includes(searchTaskValue.toLowerCase());
        }
        return item;
      }),
    [data, searchTaskValue]
  );

  const handleSaveData = () => {
    const payload = {
      task_type: selectedType,
    };
    switch (selectedType) {
      case 'pdf':
      case 'zip':
        handleUploadSourceFile({ ...payload, file: formValues?.file });
        break;
      case 'text':
        handleUploadSourceText({ ...payload, text: formValues?.text });
        break;
      case 'scraped':
      case 'compressed':
        handleUploadSourceText({ ...payload, task_id: formValues?.task });
        break;
      case 'firecrawl':
        handleUploadSourceFirecrawl({
          ...payload,
          file: formValues?.file,
          task_type: 'JSON',
        });
        break;
      default:
        break;
    }
  };

  const uploadFileLabel = useMemo(() => {
    switch (selectedType) {
      case 'pdf':
        return 'Upload PDF File';
      case 'zip':
        return 'Upload ZIP File';
      default:
        return 'Upload JSON File';
    }
  }, [selectedType]);

  const renderFileUploadtemplate = () => {
    return (
      <div className="flex-1 min-h-[200px] border border-dashed border-border rounded-md flex flex-col justify-center items-center text-medium mb-4">
        <span className="text-primary">
          <FormItem
            required
            name="file"
            label={uploadFileLabel}
            inline={false}
            renderError={() => null}
          >
            <FileUpload loading={false} />
          </FormItem>
        </span>
      </div>
    );
  };

  const renderTextTemplate = () => {
    return (
      <FormItem name="text" label="Text" inline={false}>
        <Input multiline placeholder="Paste your text here" />
      </FormItem>
    );
  };

  const renderTaskList = () => {
    return (
      <div className="flex flex-col gap-y-4">
        <FormItem name="search" label="Search" inline={false}>
          <Input
            placeholder="Search Task"
            inputClassName="pl-20"
            prefix={<SearchIcon />}
            value={searchTaskValue}
            onChange={value => setSearchTask(value)}
          />
        </FormItem>
        {isLoadingTasks ? (
          <div className="flex justify-center items-center max-h-[100px]">
            <Loading />
          </div>
        ) : (
          <FormItem name="task" label="Task List" inline={false}>
            {filteredData?.length ? (
              <RadioGroup
                name="options"
                direction="column"
                labelPosition="left"
                itemClass="flex justify-between"
                options={
                  filteredData?.map((item: any) => ({
                    id: item.id || item.task_id,
                    label: item.url || item.id || item.task_id,
                  })) || []
                }
              />
            ) : (
              <div className="flex justify-center items-center text-small">
                No Data Available
              </div>
            )}
          </FormItem>
        )}
      </div>
    );
  };

  const renderFormContent = () => {
    switch (selectedType) {
      case 'pdf':
      case 'zip':
      case 'firecrawl':
        return renderFileUploadtemplate();
      case 'text':
        return renderTextTemplate();
      case 'scraped':
      case 'compressed':
        return renderTaskList();
      default:
        return null;
    }
  };

  return (
    <div className="relative bg-white w-full rounded-xl p-6 flex min-h-[300px] overflow-hidden">
      <Form initialValue={formValues} onChange={setFormValues}>
        <div className="w-1/3 pr-6 border-r border-border flex flex-col gap-2">
          <h2 className="text-medium font-semibold mb-2">
            Select source type:
          </h2>
          {sourceTypes.map(({ label, value, icon }) => (
            <button
              key={value}
              onClick={() => setSelectedType(value)}
              className={`flex items-center w-full justify-between gap-3 p-3 rounded-md text-left transition-all duration-200
        ${
          selectedType === value
            ? 'bg-select text-primary font-semibold'
            : 'text-mainText hover:bg-background hover:text-primary'
        }
      `}
            >
              <div className="flex items-center gap-2 text-inherit">
                <span className="text-inherit flex items-center">{icon}</span>
                <span className="flex-1 text-medium">{label}</span>
              </div>

              {selectedType === value && (
                <KeyboardArrowRightIcon
                  fontSize="medium"
                  className="text-inherit"
                />
              )}
            </button>
          ))}
        </div>
        <div className="w-2/3 pl-6 flex flex-col justify-between">
          {renderFormContent()}
          <div className="flex justify-end">
            <div className="flex gap-x-md content-end items-end">
              <Button
                size="medium"
                type="contrast"
                label="Cancel"
                className="w-full text-main"
                onClick={onCancel}
              />
              <SubmitButton
                size="medium"
                disabled={isSourceCreating}
                className="w-full"
                label="Confirm"
                loading={isSourceCreating}
                onClick={handleSaveData}
                spinnerColor="background"
              />
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};
